import {
  useEffect, useId, useRef,
} from 'react'
import PropTypes from 'prop-types'
import * as amplitude from '@amplitude/analytics-browser'

// Module-level flag: persists across component mounts within a session,
// resets to false on a full page reload (module re-evaluated).
let sdkConnected = false

const WIDGET_CATEGORY = 'widget'
const WIDGET_CLICK_EVENT = 'widget_click'
// WSC fires both `widget_load` and `widget_visible` on mount; mapping both to the same
// Amplitude event would double-count. Prefer `widget_visible` as the viewability signal.
const WIDGET_VIEW_ACTION = 'widget_visible'

const getWscEventDetail = (event) => event?.detail?.eventData || event?.detail || {}
const getWidgetVariant = (detail) => detail?.Widget || detail?.widget || {}
const getStoryVariant = (detail) => detail?.Story || detail?.story || {}

const getVariantValue = (detail, key) => getWidgetVariant(detail)?.[key] ?? detail?.[key]

const normalize = (value) => String(value ?? '').trim().toLowerCase()

const pickWidgetPayload = (detail) => ({
  widget_id: getVariantValue(detail, 'widget_id'),
  widget_name: getVariantValue(detail, 'widget_name'),
  widget_type: getVariantValue(detail, 'widget_type'),
  content_id: getVariantValue(detail, 'content_id'),
  content_name: getVariantValue(detail, 'content_name'),
  content_type: getVariantValue(detail, 'content_type'),
  content_index: getVariantValue(detail, 'content_index'),
  event_category: detail.event_category,
  event_action: detail.event_action,
})

function StoriesWidget({ labels = [], contentType = 'story', contentHeight = '300' }) {
  const containerId = `wsc-container-${useId().replace(/:/g, '')}`
  const labelsRef = useRef(labels)
  const contentTypeRef = useRef(contentType)
  labelsRef.current = labels
  contentTypeRef.current = contentType

  useEffect(() => {
    let cancelled = false
    let handleSDKConnect = null
    let handleWscEventTriggered = null
    let onEventTriggeredName = null

    const initializeSDK = async () => {
      const BlazeSDK = await import('@wscsports/blaze-web-sdk')

      const basePayload = () => ({
        page_path: typeof window !== 'undefined' ? window.location.pathname : '',
        labels: labelsRef.current,
        widget_content_type: contentTypeRef.current,
      })

      const trackWscEvent = (event) => {
        const eventDetail = getWscEventDetail(event)
        const eventCategory = normalize(eventDetail.event_category)
        const eventAction = normalize(eventDetail.event_action)

        if (eventCategory === WIDGET_CATEGORY) {
          const hostContext = { host_container_id: containerId }

          if (eventAction === WIDGET_CLICK_EVENT) {
            amplitude.track('click-wsc-story-widget-card', {
              ...basePayload(),
              ...pickWidgetPayload(eventDetail),
              ...hostContext,
            })
            return
          }

          if (eventAction === WIDGET_VIEW_ACTION) {
            amplitude.track('view-wsc-story-widget', {
              ...basePayload(),
              ...pickWidgetPayload(eventDetail),
              ...hostContext,
            })
          }
          return
        }

        const configuredType = normalize(contentTypeRef.current)

        if (configuredType === 'story' && eventCategory === 'story' && eventAction === 'story_page_start') {
          const story = getStoryVariant(eventDetail)
          amplitude.track('view-wsc-story-widget-content', {
            ...basePayload(),
            wsc_event_category: eventDetail.event_category,
            wsc_event_action: eventDetail.event_action,
            story_id: story.story_id ?? eventDetail.story_id,
            story_title: story.story_title ?? eventDetail.story_title,
            story_page_id: story.story_page_id ?? eventDetail.story_page_id,
            story_page_index: story.story_page_index ?? eventDetail.story_page_index,
            story_page_count: story.story_page_count ?? eventDetail.story_page_count,
            story_page_navigation_type: story.story_page_navigation_type ?? eventDetail.story_page_navigation_type,
            story_page_navigation_direction: story.story_page_navigation_direction ?? eventDetail.story_page_navigation_direction,
            story_start_trigger: story.story_start_trigger ?? eventDetail.story_start_trigger,
          })
        }
      }

      onEventTriggeredName = BlazeSDK.Delegations.onEventTriggered
      handleWscEventTriggered = trackWscEvent
      document.addEventListener(onEventTriggeredName, handleWscEventTriggered)

      const renderWidget = () => {
        if (cancelled) return
        const myTheme = BlazeSDK.Theme('row-rectangle')
        myTheme.layoutStyle.labelStyle.fontSize = '10px'
        BlazeSDK.WidgetRowView(containerId, {
          labels: labelsRef.current, // [] - must be configured in the WSC/Blaze dashboard
          contentType: contentTypeRef.current, // moment | story | video
          theme: myTheme,
        })
      }

      if (sdkConnected) {
        // SDK already initialized from a previous page visit — the
        // onBlazeSDKConnect event won't fire again, so render directly.
        renderWidget()
      } else {
        handleSDKConnect = () => {
          sdkConnected = true
          renderWidget()
        }
        document.addEventListener('onBlazeSDKConnect', handleSDKConnect)
        await BlazeSDK.Initialize(process.env.BLAZE_SDK_API_KEY)
      }
    }

    initializeSDK()

    return () => {
      cancelled = true
      if (handleSDKConnect) {
        document.removeEventListener('onBlazeSDKConnect', handleSDKConnect)
      }
      if (onEventTriggeredName && handleWscEventTriggered) {
        document.removeEventListener(onEventTriggeredName, handleWscEventTriggered)
      }
    }
  }, [containerId])

  return <div id={containerId} style={{ width: '100%', height: `${contentHeight}px` }} />
}

StoriesWidget.propTypes = {
  labels: PropTypes.arrayOf(PropTypes.string),
  contentType: PropTypes.string,
  contentHeight: PropTypes.string,
}

export default StoriesWidget
